'use client'

/**
 * The send-feedback modal's "Start over" and its Undo (#14531).
 *
 * Start over resets the WHOLE form — the draft (message, screenshots and the
 * selected type, which lives in the draft's meta), this mount's file
 * attachments, the screenshot state the modal tracks outside the draft, the
 * recorded capture attempts, and any screenshot / paste-cap error — with no
 * confirm dialog. A "Form cleared" toast carries Undo instead: less friction
 * for a quiet ghost button, and still recoverable, which matters because
 * screenshots are lost too. Undo puts back everything but the errors, which
 * described a moment that has passed.
 *
 * Two things are DELIBERATELY KEPT: the "include page URL" checkbox and a
 * pending session recording (`usePendingSessionAttachment`). Both are context
 * about where and how the submitter is reporting, not form content they
 * wrote; a recording in particular cannot be re-made, and #13841 already
 * decided that only a submit discards it.
 *
 * Undo is dismissed on the FIRST edit after the clear — typing, picking a
 * type, attaching anything. Undo replaces the form wholesale, so left live it
 * would silently overwrite whatever was entered since.
 *
 * Undo writes back through `restoreDraft`, so the restored draft lands in
 * localStorage and `form_drafts` again rather than only in state.
 *
 * UNDO AFTER CLOSE IS A NO-OP, the simpler of the two options #14531 allows.
 * Closing the modal already drops the file attachments and screenshot display
 * (`handleClose`), so restoring after close could only ever be partial; the
 * modal also calls {@link UseFeedbackStartOverResult.dismissUndo} on close so
 * the dead button does not linger.
 *
 * `startOver` is referentially stable: it reads the latest options through a
 * ref, so the modal can pass fresh object/arrow options every render without
 * memoising them (and spending lines it does not have).
 *
 * Lives beside the modal rather than inside it because `feedback-modal.tsx`
 * sits at the 1000-line source cap (`check:file-size-ratchet`).
 */

import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { toast, toastSuccess } from '@/lib/toast'
import { trackFeedbackModeEvent } from '@/app/actions/feedback-mode-analytics'
import type { UseFeedbackDraftResult } from '@/hooks/use-feedback-draft'
import type { UseImagePasteUploadReturn } from '@/hooks/use-image-paste-upload'
import type { ScreenshotDisplay } from '@/components/feedback/feedback-modal-constants'
import type { CaptureAttempt } from '@/lib/screenshot-capture'

/** The screenshot state the modal holds outside the draft. */
export type FeedbackScreenshotState = {
  display: ScreenshotDisplay | null
  blob: Blob | null
}

export const EMPTY_SCREENSHOT_STATE: FeedbackScreenshotState = {
  display: null,
  blob: null,
}

type UseFeedbackStartOverOptions = {
  isOpen: boolean
  isAuthenticated: boolean
  draft: Pick<
    UseFeedbackDraftResult,
    'message' | 'screenshots' | 'meta' | 'clearDraft' | 'restoreDraft'
  >
  files: Pick<
    UseImagePasteUploadReturn,
    'attachments' | 'clearAttachments' | 'restoreAttachments'
  >
  screenshot: FeedbackScreenshotState
  setScreenshot: (state: FeedbackScreenshotState) => void
  /** The attempts reported as `captureMetadata` on submit. */
  captureAttemptsRef: RefObject<CaptureAttempt[]>
  /** Clear the screenshot and paste-cap errors. */
  clearErrors: () => void
}

export type UseFeedbackStartOverResult = {
  startOver: () => void
  /** Dismiss a pending "Form cleared" toast; the modal calls it on close. */
  dismissUndo: () => void
}

export function useFeedbackStartOver(
  options: UseFeedbackStartOverOptions,
): UseFeedbackStartOverResult {
  const latest = useRef(options)
  latest.current = options
  const toastIdRef = useRef<string | number | null>(null)

  const track = useCallback(
    (event: string, properties: Record<string, unknown> = {}) => {
      // The beacon no-ops without a session anyway; skip the round trip.
      if (!latest.current.isAuthenticated) return
      void trackFeedbackModeEvent(event, properties).catch(() => {})
    },
    [],
  )

  const dismissUndo = useCallback(() => {
    if (toastIdRef.current === null) return
    toast.dismiss(toastIdRef.current)
    toastIdRef.current = null
  }, [])

  const startOver = useCallback(() => {
    const { draft, files, screenshot, setScreenshot } = latest.current
    const { captureAttemptsRef, clearErrors } = latest.current
    const { message, screenshots, meta } = draft
    const prior = {
      draft: { message, screenshots, meta },
      attachments: files.attachments,
      screenshot,
      captureAttempts: [...captureAttemptsRef.current],
    }
    draft.clearDraft()
    files.clearAttachments()
    setScreenshot(EMPTY_SCREENSHOT_STATE)
    captureAttemptsRef.current = []
    clearErrors()
    // Shape only, never the message: adoption analytics is not where a
    // submitter's words belong.
    track('feedback.form_cleared', {
      hadType: meta.feedbackType !== '',
      hadScreenshot: screenshots.length > 0 || screenshot.display !== null,
      attachmentCount: prior.attachments.length,
    })

    const undo = () => {
      toastIdRef.current = null
      const current = latest.current
      if (!current.isOpen) return
      current.draft.restoreDraft(prior.draft)
      current.files.restoreAttachments(prior.attachments)
      current.setScreenshot(prior.screenshot)
      current.captureAttemptsRef.current = prior.captureAttempts
      track('feedback.form_clear_undone')
    }
    toastIdRef.current = toastSuccess('Form cleared', {
      action: { label: 'Undo', onClick: undo },
    })
  }, [track])

  // Any content arriving after a clear is a new edit — dismiss the Undo that
  // would overwrite it. The transition into "empty" (the clear itself) never
  // dismisses, and Undo nulls the toast id before it refills the form.
  const { draft, files, screenshot } = options
  const hasFormContent =
    draft.message.length > 0 ||
    draft.screenshots.length > 0 ||
    draft.meta.feedbackType !== '' ||
    files.attachments.length > 0 ||
    screenshot.display !== null
  useEffect(() => {
    if (hasFormContent) dismissUndo()
  }, [hasFormContent, dismissUndo])

  return { startOver, dismissUndo }
}
